import axios from "axios";
import { useQuery } from "@tanstack/react-query";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

export function useSite() {
  return useQuery({
    queryKey: ["site"],
    queryFn: async () => (await api.get("/site")).data,
    staleTime: 5 * 60_000,
  });
}

export function useProducts(params = {}) {
  return useQuery({
    queryKey: ["products", params],
    queryFn: async () => (await api.get("/products", { params })).data,
    staleTime: 5 * 60_000,
  });
}

export function useProduct(slug) {
  return useQuery({
    queryKey: ["product", slug],
    queryFn: async () => (await api.get(`/products/${slug}`)).data,
    enabled: !!slug,
  });
}

export function useHome() {
  return useQuery({
    queryKey: ["home"],
    queryFn: async () => (await api.get("/home")).data,
    staleTime: 2 * 60_000,
  });
}

export function useScenarios() {
  return useQuery({
    queryKey: ["scenarios"],
    queryFn: async () => (await api.get("/scenarios")).data,
    staleTime: 5 * 60_000,
  });
}

export function useMangalCalculator() {
  return useQuery({
    queryKey: ["mangal-calculator"],
    queryFn: async () => (await api.get("/mangal-calculator")).data,
    staleTime: 10 * 60_000,
  });
}

// Oturum kimliği (analitik için — tarayıcıda kalıcı)
export const SESSION_KEY = "ek_session_id";
export function getSessionId() {
  let s = localStorage.getItem(SESSION_KEY);
  if (!s) {
    s =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem(SESSION_KEY, s);
  }
  return s;
}

// Olay takibi (visit / scenario_open / list_created / whatsapp_send / package_add / product_add)
export function trackEvent(type, meta = {}) {
  return api.post("/track", { session_id: getSessionId(), type, ...meta }).catch(() => {});
}

// Sipariş kaydı (WhatsApp'a gitmeden önce, fire-and-forget)
export function createOrder(payload) {
  return api.post("/orders", payload).then((r) => r.data);
}
export function trackProductView(slug) {
  return api.post(`/products/${slug}/view`).catch(() => {});
}
export function trackProductAdd(slug) {
  return api.post(`/products/${slug}/add-event`).catch(() => {});
}export function trackPackageAdd(slug) {
  return api.post(`/packages/${slug}/add-event`).catch(() => {});
}

// FastAPI hata detayını (string | Pydantic array | object) güvenle metne çevirir.
export const apiErr = (e, fallback = "Bir hata oluştu") => {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || fallback;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join("; ");
  return typeof d === "object" ? JSON.stringify(d) : String(d);
};

export function usePackages() {
  return useQuery({
    queryKey: ["packages"],
    queryFn: async () => (await api.get("/packages")).data,
    staleTime: 5 * 60_000,
  });
}

export function usePackage(slug) {
  return useQuery({
    queryKey: ["package", slug],
    queryFn: async () => (await api.get(`/packages/${slug}`)).data,
    enabled: !!slug,
  });
}

export function useGuides() {
  return useQuery({
    queryKey: ["guides"],
    queryFn: async () => (await api.get("/guides")).data,
    staleTime: 5 * 60_000,
  });
}

export function useGuide(slug) {
  return useQuery({
    queryKey: ["guide", slug],
    queryFn: async () => (await api.get(`/guides/${slug}`)).data,
    enabled: !!slug,
  });
}

export function useCategories() {
  return useQuery({
    queryKey: ["categories"],
    queryFn: async () => (await api.get("/categories")).data,
    staleTime: 30 * 60_000,
  });
}

// Market & Şarküteri (ayrı katalog — ~715 ürün, istemci tarafı sayfalama)
export function useMarketProducts() {
  return useQuery({
    queryKey: ["market-products"],
    queryFn: async () => (await api.get("/market/products")).data,
    staleTime: 5 * 60_000,
  });
}

export function useMarketGroups() {
  return useQuery({
    queryKey: ["market-groups"],
    queryFn: async () => (await api.get("/market/groups")).data,
    staleTime: 30 * 60_000,
  });
}

export function useMarketProduct(slug) {
  return useQuery({
    queryKey: ["market-product", slug],
    queryFn: async () => (await api.get(`/market/products/${slug}`)).data,
    enabled: !!slug,
    staleTime: 5 * 60_000,
  });
}

// ---------------------------------------------------------------------------
// Admin (JWT — Bearer token, localStorage)
// ---------------------------------------------------------------------------
export const ADMIN_TOKEN_KEY = "ek_admin_token";

export const adminApi = axios.create({ baseURL: API });
adminApi.interceptors.request.use((config) => {
  const t = localStorage.getItem(ADMIN_TOKEN_KEY);
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

export async function adminLogin(username, password) {
  const { data } = await api.post("/auth/login", { username, password });
  localStorage.setItem(ADMIN_TOKEN_KEY, data.token);
  return data.user;
}
export async function adminMe() {
  const { data } = await adminApi.get("/auth/me");
  return data;
}
export function adminLogout() {
  localStorage.removeItem(ADMIN_TOKEN_KEY);
}

export const adminListProducts = () => adminApi.get("/admin/products").then((r) => r.data);
export const adminCreateProduct = (p) => adminApi.post("/admin/products", p).then((r) => r.data);
export const adminUpdateProduct = (slug, p) => adminApi.put(`/admin/products/${slug}`, p).then((r) => r.data);
export const adminDeleteProduct = (slug) => adminApi.delete(`/admin/products/${slug}`).then((r) => r.data);

export const adminListGuides = () => adminApi.get("/admin/guides").then((r) => r.data);
export const adminCreateGuide = (g) => adminApi.post("/admin/guides", g).then((r) => r.data);
export const adminUpdateGuide = (slug, g) => adminApi.put(`/admin/guides/${slug}`, g).then((r) => r.data);
export const adminDeleteGuide = (slug) => adminApi.delete(`/admin/guides/${slug}`).then((r) => r.data);

export const adminListOrders = () => adminApi.get("/admin/orders").then((r) => r.data);
export const adminUpdateOrder = (id, status) => adminApi.put(`/admin/orders/${id}`, { status }).then((r) => r.data);
export const adminDeleteOrder = (id) => adminApi.delete(`/admin/orders/${id}`).then((r) => r.data);
export const adminReports = () => adminApi.get("/admin/reports").then((r) => r.data);
export const adminAnalytics = (days = 7) =>
  adminApi.get("/admin/analytics", { params: { days } }).then((r) => r.data);
export const adminUploadImage = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return adminApi.post("/admin/upload", fd).then((r) => r.data);
};

export const adminGetSite = () => adminApi.get("/admin/site").then((r) => r.data);
export const adminUpdateSite = (body) => adminApi.put("/admin/site", body).then((r) => r.data);

export const adminListCategories = () => adminApi.get("/admin/categories").then((r) => r.data);
export const adminCreateCategory = (c) => adminApi.post("/admin/categories", c).then((r) => r.data);
export const adminUpdateCategory = (slug, c) => adminApi.put(`/admin/categories/${slug}`, c).then((r) => r.data);
export const adminDeleteCategory = (slug) => adminApi.delete(`/admin/categories/${slug}`).then((r) => r.data);

export const adminListPackages = () => adminApi.get("/admin/packages").then((r) => r.data);
export const adminCreatePackage = (p) => adminApi.post("/admin/packages", p).then((r) => r.data);
export const adminUpdatePackage = (slug, p) => adminApi.put(`/admin/packages/${slug}`, p).then((r) => r.data);
export const adminDeletePackage = (slug) => adminApi.delete(`/admin/packages/${slug}`).then((r) => r.data);

// ---------------------------------------------------------------------------
// Marka Güven Merkezi (Trust Center) — şifre korumalı, sessionStorage trust token
// ---------------------------------------------------------------------------
export const TRUST_TOKEN_KEY = "ek_trust_token";

export const trustToken = () => sessionStorage.getItem(TRUST_TOKEN_KEY);
export const trustLogout = () => sessionStorage.removeItem(TRUST_TOKEN_KEY);

export async function trustLogin(password) {
  const { data } = await api.post("/trust/auth", { password });
  sessionStorage.setItem(TRUST_TOKEN_KEY, data.token);
  return data.token;
}

export async function getTrustContents() {
  const { data } = await api.get("/trust/contents", {
    headers: { Authorization: `Bearer ${trustToken()}` },
  });
  return data;
}

// Admin — Marka Güven Merkezi yönetimi
export const adminTrustList = () => adminApi.get("/admin/trust/contents").then((r) => r.data);
export const adminTrustUpdate = (slug, body) =>
  adminApi.put(`/admin/trust/contents/${slug}`, body).then((r) => r.data);
export const adminTrustSync = () => adminApi.post("/admin/trust/sync").then((r) => r.data);
export const adminTrustSyncStatus = () =>
  adminApi.get("/admin/trust/sync-status").then((r) => r.data);

// Admin — Hızlı Fiyat Güncelleme
export const adminBulkPrice = (items) =>
  adminApi.patch("/admin/products/bulk-price", { items }).then((r) => r.data);
export const adminPriceStatus = () => adminApi.get("/admin/price-status").then((r) => r.data);

// Admin — Market Fiyat/Pasif Yönetimi
export const adminMarketList = () => adminApi.get("/admin/market/products").then((r) => r.data);
export const adminMarketBulkPrice = (items) =>
  adminApi.patch("/admin/market/bulk-price", { items }).then((r) => r.data);
export const adminMarketApply = (payload) =>
  adminApi.post("/admin/market/apply", payload).then((r) => r.data);
export const adminMarketLogs = () => adminApi.get("/admin/market/logs").then((r) => r.data);
export const adminMarketUndo = (id) =>
  adminApi.post(`/admin/market/logs/${id}/undo`).then((r) => r.data);

// Admin — Market Görsel Yönetimi
export const adminMarketImageStats = () =>
  adminApi.get("/admin/market/image-stats").then((r) => r.data);
export const adminMarketSetImage = (slug, file) => {
  const fd = new FormData();
  fd.append("file", file);
  return adminApi.post(`/admin/market/products/${slug}/image`, fd).then((r) => r.data);
};
export const adminMarketRemoveImage = (slug) =>
  adminApi.delete(`/admin/market/products/${slug}/image`).then((r) => r.data);
export const adminMarketGroupImage = (file, groupKey, onlyPlaceholder = true) => {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("group_key", groupKey);
  fd.append("only_placeholder", onlyPlaceholder ? "true" : "false");
  return adminApi.post("/admin/market/group-image", fd).then((r) => r.data);
};
